import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useCart } from "../../contexts/CartContext";
import CustomerHeader from "../../components/CustomerHeader";
import CartDrawer from "../../components/CartDrawer";
import ProductCard from "../../components/ProductCard";
import OfferCarousel from "../../components/OfferCarousel";
import { Timer, ChevronRight, Flame, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function CustomerHome() {
  const [products, setProducts] = useState([]);
  const [shops, setShops] = useState([]);
  const [cats, setCats] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [showCart, setShowCart] = useState(false);
  const navigate = useNavigate();
  const cart = useCart();

  useEffect(() => {
    (async () => {
      try {
        const [s, p, c] = await Promise.all([api.get("/shops"), api.get("/products"), api.get("/categories")]);
        setShops(s.data);
        setProducts(p.data);
        setCats(c.data.map((x) => ({ key: x.name, label: x.name, img: x.image_url })));
      } catch { toast.error("Couldn't load catalog"); }
      finally { setLoaded(true); }
    })();
  }, []);

  const shopById = useMemo(() => Object.fromEntries(shops.map((s) => [s.shop_id, s])), [shops]);

  const activeCategory = cat;

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return products.filter((p) => {
      if (activeCategory && p.category !== activeCategory) return false;
      if (needle && !p.name.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [products, activeCategory, q]);

  // group by category when no filter
  const byCategory = useMemo(() => {
    const groups = Object.fromEntries(cats.map((c) => [c.key, []]));
    for (const p of products) if (groups[p.category]) groups[p.category].push(p);
    return groups;
  }, [products, cats]);

  const topDeals = useMemo(() => {
    return [...products]
      .filter((p) => p.mrp && p.mrp > p.price)
      .sort((a, b) => (b.mrp - b.price) / b.mrp - (a.mrp - a.price) / a.mrp)
      .slice(0, 12);
  }, [products]);

  return (
    <div className="light-scope min-h-screen" data-testid="customer-home-container">
      <CustomerHeader
        onCartClick={() => setShowCart(true)}
        searchValue={q}
        onSearchChange={setQ}
      />

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-5 pb-28">
        {/* Offer carousel */}
        <div className="fade-in">
          <OfferCarousel onBannerClick={(c) => setCat(c)} />
        </div>

        {/* Quick category strip */}
        <section className="mt-6">
          <div className="flex items-end justify-between mb-3">
            <h2 className="ls-section-h text-xl sm:text-2xl">Shop by category</h2>
            {cat && (
              <button onClick={() => setCat("")} className="text-sm font-semibold text-emerald-700 hover:underline" data-testid="clear-category">
                Clear filter
              </button>
            )}
          </div>
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 scroll-snap-x" data-testid="category-strip">
            {cats.map((c) => {
              const active = cat === c.key;
              return (
                <button
                  key={c.key}
                  onClick={() => setCat(active ? "" : c.key)}
                  data-testid={`category-filter-chip-${c.label.toLowerCase().replace(" ", "-")}`}
                  className={`snap-start-x shrink-0 w-24 sm:w-28 rounded-2xl p-2 border transition ${active ? "ls-chip-active" : "ls-chip hover:border-slate-400"}`}
                >
                  <div className="aspect-square rounded-xl overflow-hidden bg-slate-50">
                    {c.img ? <img src={c.img} alt="" loading="lazy" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-2xl font-extrabold text-emerald-700">{c.label[0]}</div>}
                  </div>
                  <div className={`mt-2 text-[12px] text-center font-semibold ${active ? "text-slate-900" : "text-slate-700"}`}>
                    {c.label}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Searching or category filter — show grid */}
        {(q || cat) && (
          <section className="mt-8">
            <div className="flex items-end justify-between mb-3">
              <h2 className="ls-section-h text-xl sm:text-2xl">
                {q ? `Results for "${q}"` : cat} <span className="text-slate-500 font-medium text-sm">· {filtered.length} items</span>
              </h2>
            </div>
            {filtered.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">
                Nothing matched. Try a different term or category.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
                {filtered.map((p) => <ProductCard key={p.product_id} product={p} shop={shopById[p.shop_id]} />)}
              </div>
            )}
          </section>
        )}

        {/* Default home sections */}
        {!q && !cat && loaded && products.length === 0 && (
          <section className="mt-10 rounded-2xl border border-slate-200 bg-white p-10 text-center" data-testid="catalog-empty">
            <div className="text-lg font-extrabold text-slate-900">No shops are live near you yet</div>
            <p className="text-sm text-slate-500 mt-1">Local shops are joining STACK. Check back soon.</p>
          </section>
        )}
        {!q && !cat && products.length > 0 && (
          <>
            {/* Top Deals */}
            <section className="mt-10">
              <div className="flex items-end justify-between mb-3">
                <h2 className="ls-section-h text-xl sm:text-2xl flex items-center gap-2">
                  <Flame size={20} className="text-rose-500" /> Top Deals
                </h2>
                <button onClick={() => setCat(cats[0]?.key || "")} className="text-sm font-semibold text-emerald-700 inline-flex items-center">
                  see all <ChevronRight size={14} />
                </button>
              </div>
              <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 scroll-snap-x" data-testid="top-deals-row">
                {topDeals.map((p) => (
                  <div key={p.product_id} className="w-44 sm:w-48 shrink-0 snap-start-x">
                    <ProductCard product={p} shop={shopById[p.shop_id]} variant="scroll" />
                  </div>
                ))}
              </div>
            </section>

            {Object.entries(byCategory).map(([key, list]) => (
              list.length > 0 ? (
                <section key={key} className="mt-10">
                  <div className="flex items-end justify-between mb-3">
                    <h2 className="ls-section-h text-xl sm:text-2xl flex items-center gap-2">
                      {key === "Food" ? <Flame size={18} className="text-rose-500" /> :
                        key === "Medicine" ? <Sparkles size={18} className="text-sky-500" /> :
                        <Timer size={18} className="text-emerald-500" />}
                      {key}
                    </h2>
                    <button onClick={() => setCat(key)} className="text-sm font-semibold text-emerald-700 inline-flex items-center">
                      see all <ChevronRight size={14} />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
                    {list.slice(0, 10).map((p) => <ProductCard key={p.product_id} product={p} shop={shopById[p.shop_id]} />)}
                  </div>
                </section>
              ) : null
            ))}
          </>
        )}
      </main>

      {/* Floating cart (mobile friendly) */}
      {cart.count > 0 && (
        <button
          onClick={() => setShowCart(true)}
          data-testid="floating-cart-cta"
          className="sm:hidden fixed bottom-5 left-1/2 -translate-x-1/2 z-30 bg-emerald-600 hover:bg-emerald-700 rounded-full px-5 py-3 flex items-center gap-4 shadow-xl text-white font-bold"
        >
          <span className="text-xs uppercase tracking-widest">{cart.count} item{cart.count > 1 ? "s" : ""}</span>
          <span className="text-base">₹{cart.total}</span>
          <span className="bg-white text-emerald-700 rounded-full px-3 py-1 text-xs">View cart →</span>
        </button>
      )}

      <CartDrawer open={showCart} onClose={() => setShowCart(false)} />
    </div>
  );
}
